/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
/* eslint-disable no-nested-ternary */
import orderBy from 'lodash.orderby'
import {makeAutoObservable} from 'mobx'
import {observer} from 'mobx-react-lite'
import * as React from 'react'
import {useContext, useEffect} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {ObjUtil, SearchUtil, Util} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {Avatar} from '@components/avatar'
import {Highlight} from '@components/common/highlight/highlight'
import {Icon} from '@components/common/icon/icon'
import {Sort, Table, TableColumn} from '@components/common/table/table'
import {TextInput} from '@components/common/text-input/text-input'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {TriCheckbox} from '@components/common/tri-checkbox/tri-checkbox'
import {DraggableNative} from '@components/draggable-native'
import {multiActionsWithTasksStore} from '@components/multi-actions-with-tasks/multi-actions-with-tasks-store'
import {useMemoOne} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {globals} from '@extensions/globals-base'

export interface TableUserInfo {
  id: DataObjectId | string
  userId: DataObjectId
  userName: string
  departments: string
  position: string
  role?: string
  email?: string
  avatar?: string
  status?: string
  inDnd?: boolean
  withStatus?: boolean
  isDisallowed?: boolean
  guestId?: DataObjectId
  opacity?: number
}

export interface UserTablesProps {
  users: TableUserInfo[]
  selected: DataObjectId[]
  singleMode?: boolean
  useCheckbox?: boolean
  showSelectControls?: boolean
  onSelectedChanged?: (selected: DataObjectId[]) => void
  onToggled?: (userIds: DataObjectId[]) => void
  showPositionColumn?: boolean
  hideDepartmentColumn?: boolean
  className?: string
  lastColumn?: TableColumn<TableUserInfo>
  disableClickableRows?: boolean
  draggableUsers?: boolean
  onDragStarted?: (el?: HTMLElement) => void
  onDragStartedCallback?: () => void
  large?: boolean
  underHeadElement?: React.ReactNode
  disableDefaultSort?: boolean
  tasksOnUser?: {
    [key: DataObjectId]: 'all' | 'some' | 'none'
  }
  scrollClassName?: string
  noScroll?: boolean
  noPaddings?: boolean
}

const HighlightQuery = observer(
  (props: {text: string; className?: string; thirdText: boolean}) => {
    const [state] = useStore()
    const rootProps = useContext(PropsContext)
    return (
      <div
        className={cn(
          'whitespace-nowrap text-ellipsis overflow-hidden',
          props.thirdText && 'text-third-old',
          // eslint-disable-next-line no-nested-ternary
          props.thirdText
            ? rootProps.large
              ? 'text-12'
              : 'text-11'
            : rootProps.large
              ? 'text-14'
              : 'text-12',
          props.className
        )}
      >
        <Highlight text={props.text ?? ''} highlight={state.searchQuery} />
      </div>
    )
  }
)

type State = {
  searchQuery: string
  selected: Set<DataObjectId>
  users: TableUserInfo[]
  singleMode: boolean
  showPosition: boolean
  userElements: Map<DataObjectId, Element>
  hideDepartment: boolean
  tasksOnUser: {
    [key: DataObjectId]: 'all' | 'some' | 'none'
  }
  toggle: boolean
}

class Store {
  state: State = {
    searchQuery: '',
    selected: new Set<DataObjectId>(),
    users: [],
    singleMode: false,
    showPosition: false,
    userElements: new Map(),
    hideDepartment: false,
    tasksOnUser: {},
    toggle: true
  }

  constructor(props: UserTablesProps) {
    makeAutoObservable(this)
    this.init(props)
    this.initialSort()
  }

  initialSort() {
    if (!this.state.selected.size) {
      return
    }
    this.state.users = orderBy(this.state.users, [
      u => (u.userId === globals.currentUserId ? '0' : '1'),
      u => !this.state.selected.has(u.userId),
      u => u.userName.toLowerCase()
    ])
  }

  init(props: UserTablesProps) {
    this.state.users = orderBy(props.users, x =>
      this.state.users.findIndex(y => y.userId === x.userId)
    )
    this.state.selected = new Set(props.selected)
    this.state.singleMode = Boolean(props.singleMode)
    this.state.showPosition = Boolean(props.showPositionColumn)
    this.state.hideDepartment = Boolean(props.hideDepartmentColumn)
    this.state.tasksOnUser = props.tasksOnUser || {}
  }

  setUserElement(userId: DataObjectId, element: Element) {
    this.state.userElements.set(userId, element)
  }

  toggleSelected(userId: DataObjectId) {
    if (this.state.selected.has(userId)) {
      this.state.selected.delete(userId)
    } else {
      if (this.state.singleMode) {
        this.state.selected.clear()
      }
      this.state.selected.add(userId)
    }
  }

  get allSelected() {
    const filteredSelectedUsers = this.filteredUsers.filter(x =>
      this.state.selected.has(x.userId)
    )
    if (!filteredSelectedUsers.length) {
      return false
    }
    if (filteredSelectedUsers.length === this.filteredUsers.length) {
      return true
    }
    return undefined
  }

  setAllSelected(allSelected: boolean) {
    if (allSelected) {
      return this.selectAll()
    } else {
      return this.unselectAll()
    }
  }

  selectAll() {
    const selectUserIds = this.filteredUsers
      .filter(x => !this.state.selected.has(x.userId))
      .map(x => x.userId)
    selectUserIds.forEach(id => this.state.selected.add(id))
    return selectUserIds
  }

  unselectAll() {
    const unselectUserIds = this.filteredUsers
      .filter(x => this.state.selected.has(x.userId))
      .map(x => x.userId)
    unselectUserIds.forEach(id => this.state.selected.delete(id))
    return unselectUserIds
  }

  setSearchQuery(v: string) {
    this.state.searchQuery = v
  }

  get searchPlaceholder() {
    return this.state.hideDepartment
      ? Lang.msg.component.usersTable.searchWithoutDepartment
      : this.state.showPosition
        ? Lang.msg.component.usersTable.searchWithPosition
        : Lang.msg.component.usersTable.search
  }

  get filteredUsers() {
    return this.state.users.filter(x =>
      SearchUtil.smartSearch(
        [
          x.userName,
          !this.state.hideDepartment && x.departments,
          this.state.showPosition && x.position,
          x.role
        ]
          .filter(Boolean)
          .join(' '),
        this.state.searchQuery
      )
    )
  }

  toggleTurnOff(): void {
    this.state.toggle = false
  }
}

const StoreContext = React.createContext({} as Store)

const useStore = () => {
  const store = useContext(StoreContext)
  return [store.state, store] as const
}

const PropsContext = React.createContext({} as UserTablesProps)

const allSelected = (
  userId: DataObjectId,
  tasksOnUser: {
    [key: DataObjectId]: 'all' | 'some' | 'none'
  }
): boolean | undefined => {
  if (!tasksOnUser) {
    return false
  }

  const t = tasksOnUser[userId]

  if (t === 'none') {
    return false
  }
  if (t === 'all') {
    return true
  }
  return undefined
}

const allSelectedValue = (
  allSelected: boolean | undefined,
  tasksOnUser: {
    [key: DataObjectId]: 'all' | 'some' | 'none'
  }
) => {
  for (const value of ObjUtil.values(tasksOnUser)) {
    if (value === 'some') {
      return undefined
    }
  }
  return allSelected
}

const UserCheckbox = observer(({userId}: {userId: DataObjectId}) => {
  const [state, store] = useStore()
  const props = useContext(PropsContext)
  const userSelected =
    state.toggle && multiActionsWithTasksStore.state.visible
      ? allSelected(userId, state.tasksOnUser)
      : state.selected.has(userId)
  const onClick = () => {
    store.toggleTurnOff()
    store.toggleSelected(userId)
    props.onToggled?.([userId])
    props.onSelectedChanged?.([...state.selected])
  }
  if (props.useCheckbox) {
    return (
      <TriCheckbox
        onChange={onClick}
        stopPropagation={true}
        className={cn(!props.large && 'ml-4')}
        value={userSelected}
      />
    )
  }
  return (
    <div onClick={onClick}>
      {userSelected ? (
        <>
          <Icon
            onClick={onClick}
            className="cursor-pointer group-hover:hidden"
            name="tick"
          />
          <Icon
            onClick={onClick}
            className="hidden cursor-pointer group-hover:block"
            name="untick"
          />
        </>
      ) : (
        <Icon
          onClick={onClick}
          className="hidden cursor-pointer group-hover:block"
          name="tick-gray"
        />
      )}
    </div>
  )
})

const UserInfo = observer(({user}: {user: TableUserInfo}) => {
  const Line = ({
    title,
    children
  }: {
    title: string
    children: React.ReactNode
  }) => (
    <div className="mb-4 text-obscure-constant-old truncate">
      <span>{title}</span>: <span className="font-semibold">{children}</span>
    </div>
  )

  return (
    <div className="inline-flex text-12 text-obscure-constant-old">
      <div className="mr-8">
        <UserAvatar user={user} />
      </div>
      <div className="max-w-240">
        <div className="mb-6 font-semibold text-on-color">{user.userName}</div>
        {user.departments && (
          <Line title={Lang.msg.companies.department}>{user.departments}</Line>
        )}
        {user.position && (
          <Line title={Lang.msg.companies.position}>{user.position}</Line>
        )}
        {user.role && (
          <Line title={Lang.msg.permission.headerRole}>{user.role}</Line>
        )}
      </div>
    </div>
  )
})

const UserAvatar = ({user}: {user: TableUserInfo}) => (
  <Avatar {...user} size={20} className="shrink-0 cursor-pointer" />
)

const ToggleSelectAll = observer(() => {
  const [state, store] = useStore()
  const props = useContext(PropsContext)
  const allSelected = allSelectedValue(store.allSelected, state.tasksOnUser)
  const value =
    state.toggle && multiActionsWithTasksStore.state.visible
      ? allSelected
      : store.allSelected
  return (
    <TriCheckbox
      value={value}
      className={cn(!props.large && 'ml-4')}
      stopPropagation={true}
      onChange={value => {
        store.toggleTurnOff()
        const changedUserIds = store.setAllSelected(value)
        props.onToggled?.(changedUserIds)
        props.onSelectedChanged?.([...state.selected])
      }}
    />
  )
})

const UserPill = tw.div`
    border
    pr-6
    max-w-full
    inline-block
    border-solid
    border-transparent
    hover:border-main-old
    hover:bg-header-old
    rounded-full
`

const UserTooltip = observer((props: {user: TableUserInfo}) => {
  const [state, store] = useStore()
  return (
    <Tooltip
      onClick={e => {
        e.stopPropagation()
        store.toggleSelected(props.user.userId)
      }}
      placement="left"
      triggerTarget={state.userElements.get(props.user.userId)}
      content={<UserInfo user={props.user} />}
      zIndex={2000}
    >
      <div className="inline-block h-16"></div>
    </Tooltip>
  )
})

const createColumns = (
  {
    useCheckbox,
    draggableUsers,
    showPositionColumn,
    lastColumn,
    large,
    hideDepartmentColumn
  }: UserTablesProps,
  store: Store,
  state: State,
  props: UserTablesProps
): (TableColumn<TableUserInfo> & {
  defaultSort?: boolean
  // eslint-disable-next-line max-params
})[] => {
  const tooltipColumn: TableColumn<TableUserInfo> = {
    title: '',
    width: 0,
    className: '-mx-4',
    render: u => <UserTooltip user={u} />
  }

  const selectColumn: TableColumn<TableUserInfo> = {
    title: useCheckbox ? <ToggleSelectAll /> : '',
    width: useCheckbox ? 24 : 32,
    render: x => (
      <div className="flex items-center">
        <UserCheckbox userId={x.userId} />
      </div>
    )
  }

  const fixedColumnWidth = large ? 200 : 104

  const positionColumn: TableColumn<TableUserInfo> = {
    title: Lang.msg.companies.position,
    width: fixedColumnWidth,
    sortBy: x => x.position,
    render: x => <HighlightQuery thirdText={true} text={x.position} />
  }

  const departmentColumn: TableColumn<TableUserInfo> = {
    title: Lang.msg.companies.department,
    width: fixedColumnWidth,
    sortBy: x => x.departments,
    render: x => <HighlightQuery thirdText={true} text={x.departments} />
  }

  const restColumns: (TableColumn<TableUserInfo> & {
    defaultSort?: boolean
  })[] = [
    {
      title: <span className="ml-24">{Lang.msg.name}</span>,
      sortBy: x => {
        if (x.userId === globals.currentUserId) {
          return ''
        }
        return x.userName.toLowerCase()
      },
      defaultSort: true,
      render(user) {
        const userView = (
          <div
            ref={ref => ref && store.setUserElement(user.userId, ref)}
            className={cn(
              'flex items-center',
              draggableUsers ? 'cursor-move' : 'cursor-pointer'
            )}
            onClick={
              props.disableClickableRows
                ? e => {
                    e.stopPropagation()
                    const {userId} = user
                    store.toggleTurnOff()
                    store.toggleSelected(userId)
                    props.onToggled?.([userId])
                    props.onSelectedChanged?.([...state.selected])
                  }
                : undefined
            }
          >
            <UserAvatar user={user} />
            <HighlightQuery
              className="ml-4"
              thirdText={false}
              text={user.userName}
            />
          </div>
        )
        return draggableUsers ? (
          <DraggableNative
            onDragStarted={props?.onDragStarted}
            onDragStartedCallback={props?.onDragStartedCallback}
            transfer={{type: 'user', userId: user.userId}}
          >
            <UserPill>{userView}</UserPill>
          </DraggableNative>
        ) : (
          userView
        )
      }
    }
  ]

  return [
    tooltipColumn,
    useCheckbox && selectColumn,
    ...restColumns,
    !hideDepartmentColumn && departmentColumn,
    showPositionColumn && positionColumn,
    !useCheckbox && selectColumn,
    lastColumn
  ].filter(Util.isDefined)
}

export const UsersTable = observer((props: UserTablesProps) => {
  const store = useMemoOne(() => new Store(props), [])
  useEffect(() => {
    store.init(props)
  }, [props.users, props.selected, props.singleMode])
  const state = store.state
  const columns = createColumns(props, store, state, props)
  const defaultSortColumnIndex = columns.findIndex(x => x.defaultSort)

  const getDefaultSort = (): Sort | undefined => {
    if (props.disableDefaultSort) {
      return undefined
    }
    return props.selected.length
      ? undefined
      : {columnIndex: defaultSortColumnIndex, order: false}
  }

  const cls = props.noPaddings ? 'mb-8' : cn(props.large ? 'mb-8' : 'my-4 mx-8')

  return (
    <PropsContext.Provider value={props}>
      <StoreContext.Provider value={store}>
        <div className={cn(props.className, 'flex flex-col')}>
          <div className={cls}>
            <TextInput
              large={props.large}
              searchIcon={props.large}
              className="w-full"
              placeholder={store.searchPlaceholder}
              value={state.searchQuery}
              onChange={e => store.setSearchQuery(e)}
              autoFocus
            />
          </div>
          <Table
            defaultSort={getDefaultSort()}
            className={cn(
              'overflow-y-auto cursor-default',
              props.scrollClassName
            )}
            underHeadElement={props.underHeadElement}
            rows={store.filteredUsers.map(data => ({
              data,
              className: cn(
                !props.disableClickableRows &&
                  'cursor-pointer hover:bg-chat-list-hover-old',
                'group'
              ),
              onClick: () => {
                if (!props.disableClickableRows) {
                  store.toggleTurnOff()
                  store.toggleSelected(data.userId)
                  props.onToggled?.([data.userId])
                  props.onSelectedChanged?.([...state.selected])
                }
              }
            }))}
            columns={columns}
            noScroll={props.noScroll}
            noPaddings={props.noPaddings}
          />
        </div>
      </StoreContext.Provider>
    </PropsContext.Provider>
  )
})
